import { AxiosResponse } from 'axios';

import {
  PaymentIntent,
  UpdatePaymentIntent,
  useCreatePayment,
  useUpdatePayment,
} from '@/features/payment';
import { Currency, IntervalBillingMode, ProductTypes } from '@/types';
import {
  getPricesIdsByCurrencyAndInterval,
  // getPromotionCodeByCurrencyAndInterval,
  sortPricesByUsage,
} from '@/utils/helpers';
import storage from '@/utils/storage';

export type PaymentIntentWrapperProps = {
  createCreatePaymentMutation: ReturnType<typeof useCreatePayment>;
  createUpdatePaymentMutation: ReturnType<typeof useUpdatePayment>;
  setHasBeenReactivated: (value: boolean) => void;
  setHasBeenCanceled: (value: boolean) => void;
};

export const PaymentIntentWrapper = ({
  createCreatePaymentMutation,
  createUpdatePaymentMutation,
  setHasBeenReactivated,
  setHasBeenCanceled,
}: PaymentIntentWrapperProps) => {
  const getCreatePaymentIntent = async (
    product: ProductTypes | null | undefined,
    currency: Currency,
    intervalCount: IntervalBillingMode,
    preview: boolean,
    promotionCode: string,
    onErrorCallback: (status: number) => void
  ): Promise<PaymentIntent | undefined> => {
    if (!product || !currency) return;

    product.prices = sortPricesByUsage(product.prices);

    const prices: string[] = getPricesIdsByCurrencyAndInterval(
      product.prices,
      currency,
      intervalCount
    );

    if (!prices) {
      return;
    }

    const ajaxParams = {
      prices: prices,
      preview,
      promotionCode,
      onErrorCallback,
    };

    const paymentIntent: PaymentIntent = await createCreatePaymentMutation.mutateAsync(ajaxParams);

    clearActions();

    return paymentIntent;
  };

  const getUpdatePaymentIntent = async (
    product: ProductTypes,
    currency: Currency,
    intervalCount: IntervalBillingMode,
    promotionCode: string | '',
    onErrorCallback: (status: number, urlRedirect?: string, detail?: string) => void
  ): Promise<UpdatePaymentIntent | AxiosResponse | void> => {
    const prices: string[] = getPricesIdsByCurrencyAndInterval(
      product.prices,
      currency,
      intervalCount
    );

    if (!prices) {
      return;
    }

    const ajaxParams = {
      prices: prices,
      promotionCode,
      onErrorCallback,
    };

    const paymentIntent = await createUpdatePaymentMutation.mutateAsync(ajaxParams);
    clearActions();
    return paymentIntent;
  };

  const clearActions = () => {
    storage.clearUserData();
    setHasBeenCanceled(false);
    setHasBeenReactivated(false);
  };

  return {
    getCreatePaymentIntent,
    getUpdatePaymentIntent,
  };
};
